import { useState } from "react";
import DocumentLink from "./DocumentLink.js";

function Cell({ v }) {
  if (v && v.doc) return <DocumentLink href={v.doc} />;
  if (v && v.documentId) {
    return (
      <span className="disclosure-document-pending">
        Document pending upload
      </span>
    );
  }
  if (v && Object.prototype.hasOwnProperty.call(v, "video")) {
    return v.video ? (
      <DocumentLink href={v.video}>View video</DocumentLink>
    ) : (
      <span className="disclosure-document-pending">
        Video link unavailable
      </span>
    );
  }
  if (v && v.mail) return <a href={`mailto:${v.mail}`}>{v.mail}</a>;
  if (v && typeof v === "object") {
    return (
      <span className="disclosure-document-pending">
        Information unavailable
      </span>
    );
  }
  return v;
}

function UploadButton({
  documentId,
  hasDocument,
  isUploading,
  isBusy,
  onUpload,
}) {
  const inputId = `disclosure-upload-${documentId}`;

  return (
    <>
      <input
        id={inputId}
        className="disclosure-file-input"
        type="file"
        accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        disabled={isBusy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(documentId, file);
          event.target.value = "";
        }}
      />
      <label
        className={`disclosure-upload-button${isUploading ? " is-uploading" : ""}`}
        htmlFor={inputId}
      >
        {isUploading ? "Uploading..." : hasDocument ? "Replace" : "Upload"}
      </label>
    </>
  );
}

function isEditableCell(value) {
  return !value?.documentId;
}

function getEditableValue(value) {
  if (value?.mail !== undefined) return value.mail;
  if (value?.video !== undefined) return value.video;
  return value ?? "";
}

function updateEditableValue(value, nextValue) {
  if (value?.mail !== undefined) return { ...value, mail: nextValue };
  if (value?.video !== undefined) return { ...value, video: nextValue };
  return nextValue;
}

export default function DisclosureTable({
  title,
  head,
  rows,
  sectionId,
  canManageDocuments = false,
  canManageContent = false,
  uploadingDocumentId,
  onDocumentUpload,
  onSaveRow,
  onAddRow,
  statusMessage = "",
  onDeleteRow,
  onDeleteSection,
}) {
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [draftRow, setDraftRow] = useState(null);
  const [savingRow, setSavingRow] = useState(false);
  const [newRowKey, setNewRowKey] = useState("");
  const [newRowValue, setNewRowValue] = useState("");
  const [newRowFile, setNewRowFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [addingRow, setAddingRow] = useState(false);
  const hasDocumentRows = rows.some((row) =>
    row.some((cell) => cell?.documentId),
  );
  const showActions =
    canManageContent || (canManageDocuments && hasDocumentRows);

  const startEditing = (rowIndex, row) => {
    setEditingRowIndex(rowIndex);
    setDraftRow(row);
  };

  const cancelEditing = () => {
    setEditingRowIndex(null);
    setDraftRow(null);
  };

  const saveEditing = async () => {
    if (editingRowIndex === null || !draftRow || !onSaveRow) return;
    setSavingRow(true);
    const startedAt = Date.now();

    try {
      const didSave = await onSaveRow(sectionId, editingRowIndex, draftRow);
      const remainingTime = 500 - (Date.now() - startedAt);
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }
      if (didSave) cancelEditing();
    } catch (error) {
      // Keep the form usable if a Firebase error escapes the save handler.
      console.error("Unable to save disclosure row:", error);
    } finally {
      setSavingRow(false);
    }
  };

  const addRow = async () => {
    setAddingRow(true);
    try {
      const didAdd = await onAddRow(sectionId, {
        key: newRowKey.trim(),
        value: newRowValue.trim(),
        file: newRowFile,
      });
      if (didAdd) {
        setNewRowKey("");
        setNewRowValue("");
        setNewRowFile(null);
        setFileInputKey((current) => current + 1);
      }
    } catch (error) {
      console.error("Unable to add disclosure row:", error);
    } finally {
      setAddingRow(false);
    }
  };

  const deleteRow = async (rowIndex) => {
    if (
      !window.confirm(
        "Remove this row? This action cannot be undone from this page.",
      )
    ) {
      return;
    }
    await onDeleteRow(sectionId, rowIndex);
  };

  const deleteSection = async () => {
    if (
      !window.confirm(
        "Remove this entire section? You can restore built-in sections from Firebase if needed.",
      )
    ) {
      return;
    }
    await onDeleteSection(sectionId);
  };

  return (
    <section className="disclosure-section" id={sectionId}>
      <h3 className="disclosure-section-heading">
        <span>{title}</span>
        {canManageContent && onDeleteSection && (
          <button
            type="button"
            className="disclosure-remove-section-button"
            onClick={deleteSection}
          >
            Remove section
          </button>
        )}
      </h3>
      <div className="disclosure-table-wrap">
        <table className="disclosure-table">
          {head && (
            <thead>
              <tr>
                {head.map((h) => (
                  <th key={h}>{h}</th>
                ))}
                {showActions && <th>Action</th>}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r, rowIndex) => {
              const documentCell = r.find((cell) => cell?.documentId);
              const isEditing = editingRowIndex === rowIndex;

              return (
                <tr key={r[0] + r[1]}>
                  {r.map((c, i) => (
                    <td key={i} className={i === 0 ? "disclosure-sno" : ""}>
                      {isEditing && isEditableCell(c) ? (
                        <input
                          className="disclosure-text-input"
                          value={getEditableValue(draftRow[i])}
                          onChange={(event) =>
                            setDraftRow((current) =>
                              current.map((cell, cellIndex) =>
                                cellIndex === i
                                  ? updateEditableValue(
                                      cell,
                                      event.target.value,
                                    )
                                  : cell,
                              ),
                            )
                          }
                        />
                      ) : (
                        <Cell v={c} />
                      )}
                    </td>
                  ))}
                  {showActions && (
                    <td className="disclosure-document-action">
                      {canManageDocuments && documentCell && !isEditing && (
                        <UploadButton
                          documentId={documentCell.documentId}
                          hasDocument={Boolean(documentCell.doc)}
                          isUploading={
                            uploadingDocumentId === documentCell.documentId
                          }
                          isBusy={Boolean(uploadingDocumentId)}
                          onUpload={onDocumentUpload}
                        />
                      )}
                      {canManageContent && !isEditing && (
                        <button
                          type="button"
                          className="disclosure-edit-button"
                          onClick={() => startEditing(rowIndex, r)}
                        >
                          Edit
                        </button>
                      )}
                      {canManageContent && !isEditing && onDeleteRow && (
                        <button
                          type="button"
                          className="disclosure-remove-row-button"
                          onClick={() => deleteRow(rowIndex)}
                        >
                          Remove
                        </button>
                      )}
                      {isEditing && (
                        <div>
                          <div className="disclosure-edit-actions">
                            <button
                              type="button"
                              className="disclosure-edit-button"
                              disabled={savingRow}
                              onClick={saveEditing}
                            >
                              {savingRow ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              className="disclosure-cancel-button"
                              disabled={savingRow}
                              onClick={cancelEditing}
                            >
                              Cancel
                            </button>
                          </div>
                          {statusMessage && !savingRow && (
                            <p className="disclosure-action-status" role="alert">
                              {statusMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canManageContent && onAddRow && (
        <div className="disclosure-add-row">
          <strong>Add new row</strong>
          <div className="disclosure-add-row-fields">
            <input
              className="disclosure-text-input"
              value={newRowKey}
              placeholder="Key / row name"
              onChange={(event) => setNewRowKey(event.target.value)}
            />
            <input
              className="disclosure-text-input"
              value={newRowValue}
              placeholder="Value (optional when uploading a file)"
              onChange={(event) => setNewRowValue(event.target.value)}
            />
            <input
              key={fileInputKey}
              className="disclosure-new-file-input"
              type="file"
              accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={(event) =>
                setNewRowFile(event.target.files?.[0] || null)
              }
            />
            <button
              type="button"
              className="disclosure-edit-button"
              disabled={addingRow}
              onClick={addRow}
            >
              {addingRow ? "Adding..." : "Add row"}
            </button>
          </div>
          {statusMessage && !addingRow && (
            <p className="disclosure-action-status" role="alert">
              {statusMessage}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
